import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CaretRightIcon, CheckCircleIcon, WarningDiamondIcon, ReceiptIcon, BuildingsIcon, IdentificationCardIcon, HandHeartIcon, CertificateIcon, ScalesIcon, BriefcaseIcon } from "@phosphor-icons/react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import AppleSignInButton from "@/components/AppleSignInButton";

const ACCOUNT_TYPES = [
  {
    id: "retail_buyer",
    segment: "retail",
    title: "Retail Buyer",
    desc: "Shop from retail sellers. Place bids on auctions & buy fixed-price items.",
  },
  {
    id: "retail_seller",
    segment: "retail",
    title: "Retail Seller",
    desc: "Sell to individual retail buyers. Go live, list items, accept bids.",
  },
  {
    id: "wholesale_buyer",
    segment: "wholesale",
    title: "Wholesale Buyer",
    desc: "Source inventory in bulk from verified wholesale sellers.",
  },
  {
    id: "wholesale_seller",
    segment: "wholesale",
    title: "Wholesale Seller",
    desc: "Sell bulk lots exclusively to verified wholesale buyers.",
  },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim().toUpperCase();
  const reservationToken = (searchParams.get("reservation") || "").trim();
  const [referrer, setReferrer] = useState(null);
  const [reservation, setReservation] = useState(null); // {username, email, expires_at}
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    username: "",
    account_type: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    // Pull a saved code if visitor came in via /r/{code} earlier.
    let effectiveCode = refCode;
    if (!effectiveCode) {
      try {
        effectiveCode = (sessionStorage.getItem("bidorah_recruit_code") || "").toUpperCase();
      } catch (_) { /* ignore */ }
    }
    if (!effectiveCode) return;

    // Try BOTH attribution systems — the new open recruit program (6-char
    // alpha-num codes, milestone rewards) AND the legacy Founder-Seller
    // 2%-revenue-share program (BID-XXXXXX). Whichever matches gets shown
    // in the "invited by @x" banner. Backend `attribute_recruit_signup`
    // and Founder-Seller signup-credit run independently regardless.
    const tryRecruit = /^[A-Z2-9]{6}$/.test(effectiveCode)
      ? api.get(`/recruit/code/${encodeURIComponent(effectiveCode)}`).then((r) => ({
          code: r.data.code,
          username: r.data.recruiter?.username,
          source: "recruit",
        }))
      : Promise.reject();

    const tryLegacy = api
      .get(`/referral/validate/${encodeURIComponent(effectiveCode)}`)
      .then((r) => ({
        code: r.data.code,
        brand_name: r.data.brand_name,
        segment: r.data.segment,
        source: "founder",
      }));

    Promise.any([tryRecruit, tryLegacy])
      .then((info) => {
        setReferrer(info);
        sessionStorage.setItem("bidora_ref", info.code);
      })
      .catch(() => {
        sessionStorage.removeItem("bidora_ref");
        setReferrer(null);
      });
  }, [refCode]);

  // Resolve a `?reservation=<token>` magic link into a prefilled email +
  // locked username so the user finishes signup in one step.
  useEffect(() => {
    if (!reservationToken) return;
    api
      .get(`/username-reservations/${encodeURIComponent(reservationToken)}`)
      .then((r) => {
        setReservation(r.data);
        setForm((f) => ({
          ...f,
          email: r.data.email || f.email,
          username: r.data.username || f.username,
        }));
        toast.success(`Welcome back — your handle @${r.data.username} is held for you.`);
      })
      .catch((e) => {
        toast.error(apiError(e) || "This reservation link is no longer valid.");
      });
  }, [reservationToken]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setErr("You must agree to the Bidorah Terms & Conditions to continue.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const payload = { ...form, agreed_to_terms: true };
      if (referrer?.code) payload.referral_code = referrer.code;
      if (reservationToken) payload.username_reservation_token = reservationToken;
      await register(payload);
      sessionStorage.removeItem("bidora_ref");
      if (form.account_type === "wholesale_buyer") {
        toast.success("Account created — now submit your wholesale credentials.");
        navigate("/wholesale-credentials");
      } else {
        toast.success("Account created. Please verify your US photo ID.");
        navigate("/verify");
      }
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="register-page">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-12">
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
          Choose your lane. Choose wisely.
        </h1>
        <p className="text-zinc-600 mt-3 max-w-2xl">
          Bidorah strictly separates retail and wholesale. Your account type is{" "}
          <span className="font-bold">permanent</span> and cannot be changed later.
          Wholesale cannot sell to retail. No exceptions.
        </p>
        <Link
          to="/why-bidorah"
          className="inline-flex items-center gap-2 mt-3 text-sm text-[#1D78AB] hover:underline font-semibold"
          data-testid="register-why-bidorah-link"
        >
          Why Bidorah &amp; not the others? <span className="text-base">→</span>
        </Link>

        {referrer && (
          <div
            className="mt-6 flex items-start gap-3 rounded-2xl border-2 border-[#1D78AB] bg-[#1D78AB]/5 p-4"
            data-testid="referral-banner"
          >
            <div className="h-10 w-10 rounded-xl bg-[#1D78AB] text-white grid place-items-center flex-shrink-0">
              <HandHeartIcon size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="overline text-[#1D78AB]">
                {referrer.source === "recruit" ? "INVITED BY A FRIEND" : "INVITED BY A FOUNDER SELLER"}
              </p>
              <p className="font-display font-bold text-lg mt-1 truncate">
                {referrer.source === "recruit"
                  ? `@${referrer.username} invited you to Bidorah`
                  : `${referrer.brand_name} invited you to Bidorah`}
              </p>
              <p className="text-xs text-zinc-600 mt-1">
                {referrer.source === "recruit"
                  ? "Sign up below — they'll earn rewards as you hit each milestone."
                  : "You'll land on their storefront after signup."}{" "}
                Referral code{" "}
                <span className="font-mono font-bold text-[#1D78AB]">{referrer.code}</span> is
                locked in.
              </p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-10">
            {/* Google sign-in fast-path — buyers don't need to fill in
                forms; one tap creates a retail-buyer account and lands
                them at /verify. They can switch type or upgrade to
                seller from the dashboard later. */}
            <div className="max-w-md mb-6">
              <div className="space-y-3">
                <GoogleSignInButton label="Sign up with Google" testid="register-google-btn" />
                <AppleSignInButton label="Sign up with Apple" testid="register-apple-btn" />
              </div>
              <div className="mt-5 flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-zinc-200" />
                <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                  or pick an account type
                </span>
                <span className="h-px flex-1 bg-zinc-200" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {ACCOUNT_TYPES.map((t) => {
                const active = form.account_type === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => update("account_type", t.id)}
                    className={`text-left p-6 border transition-all ${
                      active
                        ? "border-[#1D78AB] bg-[#1D78AB]/5 shadow-[0_0_0_3px_rgba(29,120,171,0.1)]"
                        : "border-zinc-200 hover:border-zinc-400 bg-white"
                    }`}
                    data-testid={`account-type-${t.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`px-2 py-1 text-[10px] font-bold tracking-widest uppercase ${
                          t.segment === "wholesale" ? "bg-amber-500 text-white" : "bg-[#1D78AB] text-white"
                        }`}
                      >
                        {t.segment}
                      </span>
                      {active && <CheckCircleIcon size={22} weight="fill" className="text-[#1D78AB]" />}
                    </div>
                    <div className="font-display font-bold text-lg">{t.title}</div>
                    <p className="text-sm text-zinc-600 mt-1">{t.desc}</p>
                  </button>
                );
              })}
            </div>

            {form.account_type === "wholesale_buyer" && (
              <div
                className="mt-6 border-2 border-amber-500 bg-amber-50 rounded-2xl p-6 shadow-lg"
                data-testid="wholesale-buyer-requirements"
              >
                <div className="flex items-start gap-3">
                  <WarningDiamondIcon
                    size={28}
                    weight="fill"
                    className="text-amber-600 flex-shrink-0 mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="font-display font-black text-lg tracking-tight text-amber-900">
                      Wholesale buyers: required before you can purchase.
                    </p>
                    <p className="text-sm text-amber-900/80 mt-1">
                      Only qualified customers who apply to buy from wholesalers can purchase from
                      wholesalers on Bidorah. You must provide the following — <b>no exceptions</b>.
                    </p>

                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      <div className="bg-white border border-amber-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <ReceiptIcon size={16} className="text-amber-600" />
                          <p className="font-semibold text-sm">Reseller's Permit</p>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          Lets you buy from wholesalers without paying sales tax. Apply through your
                          state's tax authority.
                        </p>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <IdentificationCardIcon size={16} className="text-amber-600" />
                          <p className="font-semibold text-sm">Taxpayer ID (TIN)</p>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          Required for tax purposes, especially when collecting sales tax from your
                          customers. EIN or SSN.
                        </p>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-xl p-3 sm:col-span-2">
                        <div className="flex items-center gap-2 mb-1">
                          <BuildingsIcon size={16} className="text-amber-600" />
                          <p className="font-semibold text-sm">Registered Business Entity</p>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          Sole proprietorship, LLC, S-Corp, C-Corp, partnership, or nonprofit —
                          registered with your state. You'll submit your business name + state after
                          signup.
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-amber-900 mt-4 font-semibold">
                      Next step: after creating your account, we'll take you straight to the
                      credentials form. Until approved, you cannot bid on or purchase wholesale
                      listings.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {form.account_type === "wholesale_seller" && (
              <div
                className="mt-6 border-2 border-amber-500 bg-amber-50 rounded-2xl p-6 shadow-lg"
                data-testid="wholesale-seller-requirements"
              >
                <div className="flex items-start gap-3">
                  <WarningDiamondIcon
                    size={28}
                    weight="fill"
                    className="text-amber-600 flex-shrink-0 mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="font-display font-black text-lg tracking-tight text-amber-900">
                      Wholesale sellers: required before we approve you.
                    </p>
                    <p className="text-sm text-amber-900/80 mt-1">
                      Only qualified wholesalers that apply to sell wholesale on Bidorah must
                      provide the following information. <b>No exceptions.</b>
                    </p>

                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      <div className="bg-white border border-amber-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <CertificateIcon size={16} className="text-amber-600" weight="fill" />
                          <p className="font-semibold text-sm">Licensing</p>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          Wholesalers need to obtain the appropriate licenses and permits, which
                          can vary by state and product type.
                        </p>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <ReceiptIcon size={16} className="text-amber-600" weight="fill" />
                          <p className="font-semibold text-sm">Sales Tax</p>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          Must collect sales tax on non-exempt transactions and may need to
                          register for a sales tax permit.
                        </p>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <BuildingsIcon size={16} className="text-amber-600" weight="fill" />
                          <p className="font-semibold text-sm">Zoning & Building Codes</p>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          Many areas have zoning restrictions that prohibit retail foot traffic,
                          which can affect where wholesalers can sell to the public.
                        </p>
                      </div>
                      <div className="bg-white border border-amber-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <ScalesIcon size={16} className="text-amber-600" weight="fill" />
                          <p className="font-semibold text-sm">Consumer Protection Laws</p>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          Must comply with consumer protection laws that apply to B2B sales but
                          not to direct sales to consumers.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 bg-amber-100/70 border border-amber-300 rounded-xl p-4 flex gap-3">
                      <BriefcaseIcon
                        size={22}
                        weight="fill"
                        className="text-amber-700 flex-shrink-0 mt-0.5"
                      />
                      <p className="text-xs text-amber-900 leading-relaxed">
                        Wholesalers primarily operate on a{" "}
                        <b>business-to-business (B2B) model</b>, selling large quantities of goods
                        to retailers or other businesses rather than individual consumers. This
                        approach allows them to profit from bulk sales, which would not be
                        economical if they sold smaller quantities to the public — per-unit costs
                        would be higher and margins lower.
                      </p>
                    </div>

                    <p className="text-xs text-amber-900 mt-4 font-semibold">
                      After approval, we'll request digital copies of your licenses, sales tax
                      registration, and business formation documents. Until verified, wholesale
                      listings and checkout remain blocked.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <Link to="/login" className="text-sm text-zinc-600">
                Already have an account?{" "}
                <span className="text-[#1D78AB] font-semibold">Sign in</span>
              </Link>
              <Button
                disabled={!form.account_type}
                onClick={() => {
                  setStep(2);
                  // Reset to the top of the page so the email/password
                  // fields are immediately visible. Otherwise the browser
                  // preserves the prior scroll position from step 1 and
                  // the user lands mid-page.
                  if (typeof window !== "undefined") {
                    window.scrollTo({ top: 0, behavior: "instant" });
                  }
                }}
                className="bg-[#1D78AB] hover:bg-[#165d85] text-white gap-1"
                data-testid="register-next-btn"
              >
                Continue <CaretRightIcon size={16} />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-10 max-w-lg">
            {reservation && (
              <div
                className="mb-4 rounded-2xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3 flex items-start gap-3"
                data-testid="reservation-banner"
              >
                <span className="mt-0.5 inline-flex h-6 w-6 grid place-items-center rounded-full bg-emerald-500 text-white text-xs font-bold">
                  ✓
                </span>
                <div className="text-sm">
                  <div className="font-semibold text-emerald-900">
                    Your handle <span className="font-mono">@{reservation.username}</span> is locked in.
                  </div>
                  <div className="text-emerald-800 mt-0.5">
                    Finish signup to claim it permanently.
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={submit} className="space-y-4" data-testid="register-form">
              <div>
                <Label htmlFor="username" className="overline">Username (public)</Label>
                <div className="mt-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">@</span>
                  <Input
                    id="username"
                    value={form.username}
                    onChange={(e) =>
                      update(
                        "username",
                        e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20)
                      )
                    }
                    required
                    disabled={!!reservation}
                    minLength={3}
                    maxLength={20}
                    pattern="[a-z0-9_]{3,20}"
                    placeholder="bidqueen99"
                    className="pl-7"
                    data-testid="register-username-input"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-1.5">
                  {reservation
                    ? "Locked from your reservation — your handle is reserved for you."
                    : "This is what other users see on bids, listings, and live streams. Lowercase letters, numbers, or underscores. 3–20 characters."}
                </p>
              </div>
              <div>
                <Label htmlFor="name" className="overline">Full Name (private)</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  className="mt-1"
                  data-testid="register-name-input"
                />
                <p className="text-xs text-zinc-500 mt-1.5">
                  Your real legal name — only visible to you and Bidorah staff.
                  Used for ID verification, payouts, and shipping. Never shown to
                  other users.
                </p>
              </div>
              <div>
                <Label htmlFor="email" className="overline">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  required
                  className="mt-1"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="overline">Password (6+ characters)</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  required
                  minLength={6}
                  className="mt-1"
                  data-testid="register-password-input"
                />
              </div>
              {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
              {form.account_type === "wholesale_buyer" ? (
                <div className="p-3 border-2 border-amber-500 bg-amber-50 rounded-xl text-sm" data-testid="wholesale-next-step-callout">
                  <span className="overline text-amber-700">NEXT STEP — REQUIRED</span>
                  <p className="mt-1 text-amber-900">
                    After creating your account, you'll be taken to the wholesale credentials form to
                    submit your <b>reseller's permit</b>, <b>TIN</b>, and <b>registered business
                    entity</b>. Until we approve these, wholesale bidding and checkout are blocked.
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 border border-amber-300 bg-amber-50 text-sm rounded-xl">
                  <span className="overline text-amber-600">NOTE</span>
                  After signup you must upload a US government photo ID to activate your account.
                </div>
              )}
              <div
                className="flex items-start gap-2.5 p-3 rounded-xl border border-zinc-200 bg-zinc-50"
                data-testid="terms-agreement-block"
              >
                <Checkbox
                  id="terms-agree"
                  checked={agreedToTerms}
                  onCheckedChange={(v) => setAgreedToTerms(v === true)}
                  className="mt-0.5 data-[state=checked]:bg-[#1D78AB] data-[state=checked]:border-[#1D78AB]"
                  data-testid="terms-agree-checkbox"
                />
                <Label
                  htmlFor="terms-agree"
                  className="text-xs text-zinc-700 font-normal leading-relaxed cursor-pointer"
                >
                  I have read and agree to the{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-[#1D78AB] hover:underline"
                    data-testid="terms-link-inline"
                  >
                    Bidorah Terms &amp; Conditions
                  </Link>
                  . I confirm I am 18 or older and agree to Bidorah&rsquo;s
                  retail / wholesale segregation rules, mandatory US photo-ID
                  verification, and platform fee policies.
                </Label>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    if (typeof window !== "undefined") {
                      window.scrollTo({ top: 0, behavior: "instant" });
                    }
                  }}
                  data-testid="register-back-btn"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !agreedToTerms}
                  className="flex-1 bg-[#1D78AB] hover:bg-[#165d85] text-white disabled:opacity-50"
                  data-testid="register-submit-btn"
                >
                  {loading ? "Creating..." : "Create account"}
                </Button>
              </div>
            </form>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
